import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

const DRIVER_KEYS = {
  maturity: 'trust.driverMaturity',
  sector: 'trust.driverSector',
  locations: 'trust.driverLocations',
  itSystems: 'trust.driverItSystems',
  infrastructure: 'trust.driverInfrastructure',
  controlReuse: 'trust.driverControlReuse',
  deliveryModel: 'trust.driverDeliveryModel',
  labourRates: 'trust.driverLabourRates',
};

/** "Your biggest cost drivers" — modelled impact vs a neutral reference scenario. */
export function CostDriversPanel({ drivers = [], formatCost }) {
  const { t } = useTranslation();
  const maxImpact = Math.max(1, ...drivers.map(d => Math.abs(d.modelledCostDelta)));

  return (
    <section className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="cost-drivers-panel">
      <header className="px-5 py-4 border-b border-[#DDE1E5]">
        <h3 className="text-sm font-semibold text-[#171A1D]">{t('trust.driversTitle')}</h3>
        <p className="mt-1 text-xs text-[#8A929B]">{t('trust.driversDesc')}</p>
      </header>

      {drivers.length === 0 ? (
        <div className="px-5 py-6" data-testid="cost-drivers-empty">
          <div className="text-sm font-medium text-[#3A4249]">{t('trust.driverNoneTitle')}</div>
          <p className="mt-1 text-xs text-[#66707A]">{t('trust.driverNone')}</p>
        </div>
      ) : (
        <ul x-excluded="true" className="divide-y divide-[#DDE1E5]">
          {drivers.map(driver => {
            const increases = driver.modelledCostDelta > 0;
            const days = Math.abs(driver.modelledEffortDelta);
            let Icon = Minus;
            if (driver.modelledCostDelta > 0) Icon = ArrowUpRight;
            else if (driver.modelledCostDelta < 0) Icon = ArrowDownRight;
            const tone = increases ? 'text-[#3A4249]' : 'text-[#527F19]';
            return (
              <li key={driver.id} className="px-5 py-3.5" data-testid={`cost-driver-${driver.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#171A1D] truncate">
                      {t(DRIVER_KEYS[driver.id] || driver.id)}
                    </div>
                    <div className="mt-0.5 text-xs text-[#66707A]">
                      {increases
                        ? t('trust.driverAdds', { days })
                        : t('trust.driverSaves', { days })}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-semibold shrink-0 ${tone}`}
                       style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    {increases ? '+' : '−'}{formatCost(Math.abs(driver.modelledCostDelta))}
                  </div>
                </div>
                <div className="mt-2 h-1 w-full bg-[#F5F6F7] rounded-sm overflow-hidden" aria-hidden="true">
                  <div
                    className={increases ? 'h-full bg-[#8A929B]' : 'h-full bg-[#78B928]'}
                    style={{ width: `${Math.max(4, (Math.abs(driver.modelledCostDelta) / maxImpact) * 100)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
